import Dexie, { type EntityTable,liveQuery } from 'dexie';

// Imported from the deep source path rather than the `@ping/ui` barrel:
// these reaction types are additive and the barrel (`packages/ui/src/sync/index.ts`)
// is owned by another module, so we reference the source file directly to
// keep this file self-contained. The `@ping/ui` package symlinks to
// `packages/ui`, so this resolves through normal node module resolution.
import type {
  MeetingEventRecord,
  MeetingEventsPersister,
  NoteRecord,
  NotesPersister,
  PollVoteRecord,
  PollVotesPersister,
  ReactionRecord,
  ReactionsPersister,
  ReadPointerRecord,
  ReadPointersPersister,
  ReminderRecord,
  RemindersPersister,
  TaskRecord,
  TasksPersister,
  TourSeenPersister,
} from '@ping/ui/src/sync/persistenceAdapters';

export interface Account {
  id: string;
  phoneE164?: string;
  email?: string;
  deletedAt?: number;
  createdAt: number;
  displayName?: string;
}

export interface Device {
  id: string;
  accountId: string;
  lastSeenAt: number;
  platform?: string;
  pushToken?: string;
}

export interface Conversation {
  id: string;
  kind: 'direct' | 'group';
  name?: string;
  archivedAt?: number;
  /**
   * Per-conversation pin marker (`Date.now()` when pinned, cleared when not).
   * Pinned conversations sort to the top of the active list. Like the other
   * personal-state flags it's synced across the user's devices via the
   * device-group `conv_state` patch. Not indexed (sorting happens in JS), so
   * no schema-version bump is needed.
   */
  pinnedAt?: number;
  /**
   * Soft-delete marker. Set (to `Date.now()`) when the user deletes the chat:
   * the message history is cleared and the row is hidden from the list, but the
   * IDENTITY (name, avatar, kind, roster, peerAccountId) is preserved so a later
   * message/Welcome can resume the chat with correct info. Cleared (to `0`) when
   * new activity arrives. Non-indexed; `> 0` = hidden, `undefined`/`0` = visible.
   */
  deletedAt?: number;
  /**
   * Set (to `Date.now()`) when the SERVER authoritatively 404s the conversation
   * (the transport reports N consecutive misses → it's an MLS group the backend
   * doesn't know about: a leaked call group, a group the peer deleted/left, or a
   * dead invite). Distinct from `deletedAt` (a USER delete): a re-emitted Welcome
   * on resume must NEVER un-hide an orphan (that caused the flapping "New chat"
   * ghost), so `persistIfNew` leaves `orphanedAt` alone. Only a genuine revive
   * clears it: the server LISTING the conversation again (reconcile) or a real
   * inbound message. `> 0` = hidden. Non-indexed → no schema-version bump.
   */
  orphanedAt?: number;
  /**
   * Set (to `Date.now()`) when the local user LEFT a group. Unlike `deletedAt`
   * this is NON-destructive: the row + all message history stay so the user can
   * still read the old conversation; it only marks membership as ended so the
   * composer goes read-only. Never touches `db.messages`. `> 0` = left,
   * `undefined`/`0` = active member. Non-indexed → no schema-version bump.
   */
  leftAt?: number;
  /**
   * The MLS epoch at which the local user LEFT (captured from the leave broadcast's
   * send). The re-invite detector clears `leftAt` only when a live membership
   * update arrives at an epoch STRICTLY GREATER than this — i.e. membership
   * genuinely advanced after we left. This is what makes leave/re-invite
   * replay-safe: a replayed OLD `Welcome` on reconnect carries an epoch `<=`
   * `leftAtEpoch`, so it can never falsely reactivate the composer, whereas a real
   * re-add lands at a higher epoch. Non-indexed → no schema-version bump.
   */
  leftAtEpoch?: number;
  /**
   * Account ids that LEFT / were removed from this group, remembered locally so
   * the authoritative server-roster reconcile can't transiently RE-ADD a leaver
   * during the window before their MLS self-remove proposal is committed and the
   * server drops them. Cleared per-account on a genuine re-invite (the member
   * reappears in a `com.ping.group_name` roster). Non-indexed → no schema bump.
   */
  departedAccountIds?: string[];
  /**
   * Per-field monotonic clock for cross-device `com.ping.conv_state` sync:
   * logical-field key → the HLC `sentAt` of the newest patch applied for that
   * field. `applyConvStatePatch` drops any patch whose `eventTs` is not newer,
   * so out-of-order device-group patches can no longer flip-flop archive / pin /
   * delete / left / mute / favorite state. Non-indexed; local; no migration.
   */
  convStateAppliedAt?: Record<string, number>;
  lastMessageAt?: number;
  /**
   * Truncated text of the most recent message in the conversation —
   * what the chat-list row's "John: hey, are you free?" preview reads.
   * Updated by both the outgoing send path (`persistSentMessage`) and
   * the incoming envelope drain (`syncEngine.drainQueue`) so the
   * preview stays current in real time. Capped at ~120 chars to keep
   * Dexie rows small (the chat row only renders ~50 chars anyway).
   */
  lastMessagePreview?: string;
  /**
   * `accountId` of the sender of the most recent message. Used by the
   * chat-list row to disambiguate self vs peer (e.g. "You: hey" vs
   * "John: hey"). Same write paths as `lastMessagePreview`.
   */
  lastMessageSenderAccountId?: string;
  createdAt: number;
  // For 1:1 ("direct") conversations: the peer's account_id. Captured at
  // chat-creation time so the MLS-invite flow can fetch the peer's
  // KeyPackage + device list and add them via conv.addMembers() before
  // the first application message goes out. Unset for group chats.
  peerAccountId?: string;
  // True when this device joined the conversation via an incoming
  // Welcome (i.e. someone else invited *us*), as opposed to
  // initiating the chat ourselves through NewChatModal. The chat
  // list uses this to surface a "new chat" indicator only on the
  // receiver's row — the inviter doesn't need it since they just
  // started the chat themselves.
  joinedViaInvite?: boolean;
  /**
   * Per-conversation mute. When true, new messages in this conversation
   * never raise a banner/sound/OS notification (the unread badge still
   * counts). @mentions override mute. Non-indexed — no Dexie version bump.
   */
  isMuted?: boolean;
  /**
   * Per-conversation notification LEVEL (local, per-device): 'all' (alert on
   * every message), 'mentions' (alert only on @mentions), or 'off' (hard
   * silence — even @mentions only bump the badge). Undefined = 'all'. This is
   * the granular form of `isMuted`; the notification gate honours both. Set
   * from Chat Info → Notifications. Non-indexed; local-only by design.
   */
  notificationLevel?: 'all' | 'mentions' | 'off';
  /**
   * Per-conversation FAVORITE marker (local, per-device). Surfaced in the
   * Chat Info → Profile "Favorite" toggle. Non-indexed — no Dexie version
   * bump. Local-only by design (not an MLS app-event): it's a personal view
   * preference, like mute/archive, so it does not sync to peers.
   */
  favorite?: boolean;
  /**
   * Per-conversation NICKNAME / alias (local, per-device). Shown only to this
   * user as an alternative display name. Non-indexed — no Dexie version bump.
   * Local-only by design (a private alias must never leak to the peer).
   */
  nickname?: string;
  /**
   * Disappearing-messages TTL in seconds for this conversation, set by a
   * `com.ping.disappearing_policy` app-event (types-and-schemas §2.13). 0 /
   * undefined = off. Non-indexed. Synced to peers via the app-event; this row
   * mirrors the latest policy so new + existing messages get an expiry.
   */
  disappearingTtlSeconds?: number;
  /**
   * For `kind: 'group'`: accountId → group role (`owner`/`admin`/`member`),
   * driven by `com.ping.member_role` app-events. Non-indexed.
   */
  memberRoles?: Record<string, 'owner' | 'admin' | 'member'>;
  /**
   * C3: the group OWNER (creator) account id, from the authenticated MLS group
   * meta inviter. Immutable; bootstraps role authority so a role/wipe event is
   * honored only from the owner or an admin. Non-indexed.
   */
  ownerAccountId?: string;
  /**
   * Per-device "I verified this peer's safety number" flag (Chat Info →
   * Security → Verify identity). Local-only. Non-indexed.
   */
  safetyVerified?: boolean;
  /**
   * 1:1 peer's Persona KYC identity-verified status (their profile's
   * `identity_verified`), hydrated from the directory. Drives the chat-list
   * verified check. Non-indexed; refreshed by `hydrateMemberProfiles`.
   */
  identityVerified?: boolean;
  /**
   * Group/conversation avatar (data or remote URL). Set when a group is
   * created with a photo and editable from Chat Info. Non-indexed; local.
   */
  avatarUri?: string;
  /**
   * Stable media id (base64url) of the group/conversation avatar, carried in the
   * MLS group meta. `avatarUri` above is a SHORT-LIVED presigned download URL
   * resolved from this id; it expires (~5 min), so the id is kept on the row to
   * re-resolve a fresh `avatarUri` on every hydration pass (boot / open /
   * reconnect). Non-indexed; local.
   */
  avatarMediaId?: string;
  /**
   * For `kind: 'group'`: sorted unique peer `account_id` values
   * (base64url), excluding the local user. Used for create-time dedup
   * and member counts.
   */
  memberAccountIds?: string[];
  /**
   * DURABLE explicit-group marker. Set to `true` the moment a row is POSITIVELY
   * identified as a group — created locally as a group (`ensureGroupConversation`)
   * or a received conversation whose MLS meta parses as a group (`sp:true` /
   * title / group avatar / >1 member). Once set it is NEVER cleared, so a
   * conversation that was ever a group can NEVER be collapsed into a same-peer
   * 1:1 by the direct-dedup passes (see `isRealGroup`). This is the authoritative,
   * race-free separator between a single-peer GROUP and a 1:1 DM (which are
   * otherwise indistinguishable by roster alone). Non-indexed → no schema bump.
   */
  explicitGroup?: boolean;
  /** device hex (inbound sender) → display name from `ping.profile`. */
  memberDeviceNames?: Record<string, string>;
  /** accountId → display name for group roster / mentions. */
  memberDisplayNames?: Record<string, string>;
  /** device hex → peer account id. */
  memberDeviceAccounts?: Record<string, string>;
  /**
   * accountId → resolved (presigned) avatar URL for each group member.
   * Persisted so a member's picture renders INSTANTLY on reload and while
   * offline, instead of re-fetching the directory every time the roster /
   * member bubbles show. Written by the accountId-keyed member-profile
   * hydrator (see features/chat/memberProfileHydration.ts) — never blanked,
   * only replaced with a newer non-empty URL. Non-indexed; local.
   */
  memberAvatarUris?: Record<string, string>;
}

export interface Message {
  id: string;
  conversationId: string;
  epoch: number;
  serverSeq: number;
  contentHash: string;
  senderAccountId: string;
  /** Hex of the sender's MLS UserId. With shared-identity, a sibling device's
   *  messages carry OUR UserId → bulletproof `isSelf`. Non-indexed; absent on
   *  legacy rows. */
  senderUserIdHex?: string;
  body?: string;
  // [#2] Raw decrypted AppEvent envelope bytes — see `MessageRecord.appEventBytes`
  // in packages/ui. Persisted so peer history-transfer can replay faithfully on
  // a linked device. Non-indexed (append-only field, no schema-version bump).
  appEventBytes?: Uint8Array;
  deletedAt?: number;
  /**
   * Wall-clock ms after which a disappearing message self-destructs. Stamped
   * (= sentAt + conversation TTL) when the conversation has a disappearing
   * policy. A periodic sweep hard-deletes rows past this. Non-indexed.
   */
  expiresAt?: number;
  sentAt: number;
  // Delivery state for outgoing messages:
  //   - "pending": handler accepted the text, send is in-flight
  //   - "sent": conv.send resolved without error; envelope on the wire
  //   - "failed": conv.send threw; user can retry via the bubble button
  // Incoming messages (peer-authored) leave this unset; the UI treats
  // unset as "delivered" since we have no failure signal for them.
  deliveryState?: 'pending' | 'sent' | 'failed';
  // Wall-clock ms timestamp the peer ACK'd receipt of the message but
  // hasn't read it yet. Drives the bubble's single-check icon (Figma
  // 2804:26325). Stamped by the `com.ping.delivered` receipt handler
  // in `useIncomingMessages` via `messages.update(id, { deliveredAt })`.
  // No Dexie index needed — the UI reads it as a passthrough on the
  // row keyed by id / contentHash. Append-only on the row schema, so
  // no version bump.
  deliveredAt?: number;
  // Reply-to anchor — see `MessageRecord.inReplyTo` in @ping/ui. Stored
  // as a plain object on the row (non-indexed, no version bump for the
  // field itself; the v7 bump below is for the reactions table). Drives
  // the quoted-preview header the MessageBubble renders above the body.
  inReplyTo?: { messageId: string; preview: string; senderName?: string };
  // Wall-clock ms of the latest edit — see `MessageRecord.editedAt`.
  // Last-writer-wins on `body`; presence flips the bubble's "edited"
  // affordance. Non-indexed passthrough.
  editedAt?: number;
  // Wall-clock ms the message was redacted — see `MessageRecord.redactedAt`.
  // Monotonic: once set the bubble renders a tombstone. Non-indexed.
  redactedAt?: number;
}

// OR-set reaction element persisted on web — mirrors `ReactionRecord`
// in @ping/ui. The compound primary key
// `[conversationId+targetContentHash+emoji+actorAccountId]` makes
// `put` idempotent (re-adding the same reactor's emoji is a no-op) and
// `deleteByKey` a single-row delete. The secondary
// `[conversationId+targetContentHash]` index lets the projection scan a
// single message's reactions cheaply; `conversationId` alone backs
// `listByConversation` + the liveQuery subscription.
export interface Reaction {
  id: string;
  conversationId: string;
  targetContentHash: string;
  targetMessageId?: string;
  emoji: string;
  actorAccountId: string;
  addedAt: number;
}

// OR-set poll-vote element persisted on web — mirrors `PollVoteRecord`
// in @ping/ui. Keyed on the stable `id`
// (`${pollTarget}:${actorAccountId}:${optionIndex}`) so an actor can hold
// MULTIPLE rows on a multi-select poll (one per chosen option); single-
// choice is the special case of exactly one row per actor. The
// `[pollTarget+actorAccountId]` compound index backs `deleteByActor` /
// `replaceActorVotes` (delete ALL of an actor's rows by field, clearing
// legacy `${pollTarget}:${actorAccountId}` single-choice rows too); the
// `pollTarget` index backs `listByPoll` + the liveQuery subscription.
export interface PollVote {
  id: string;
  pollTarget: string;
  actorAccountId: string;
  optionIndex: number;
  votedAt: number;
}

/** One captured meeting event (§2.13) — see `MeetingEventRecord`. */
export interface MeetingEvent {
  id: string;
  conversationId: string;
  meetingId: string;
  kind: 'create' | 'update' | 'rsvp' | 'cancel';
  senderAccountId: string;
  /** Raw sender device hex + account-level MLS UserId — non-indexed; kept so the
   *  fold can re-resolve the account from the current roster (see the persister
   *  interface). Absent on optimistic self-writes. */
  senderDeviceId?: string;
  senderUserIdHex?: string;
  at: number;
  payloadJson: string;
}

// Device-local meeting reminder (§2.13). `reminderMinutes` is NOT on the wire —
// it's the creator's per-device "remind me N minutes before" choice — so it
// lives only here, keyed on `meetingId`. `useMeetingReminderScheduler` arms a
// local Notification at `startsAt - reminderMinutes*60_000`; `firedAt` guards
// against re-firing on reload. Deleted when the meeting is canceled.
export interface MeetingReminder {
  meetingId: string;
  conversationId: string;
  title: string;
  startsAt: number;
  reminderMinutes: number;
  firedAt?: number;
}

// Personal task persisted on web — mirrors `TaskRecord` in @ping/ui.
// DeviceGroup-only personal state (§2.10). Keyed on the stable hex
// `task_id` (`id`). No secondary indexes — the whole list is read at
// once and sorted by `position` in `useTasks`.
export interface Task {
  id: string;
  title: string;
  due?: number;
  done?: boolean;
  list?: string;
  position: string;
  updatedAt: number;
}

// Personal note persisted on web — mirrors `NoteRecord` in @ping/ui.
// DeviceGroup-only personal state (§2.10). Keyed on the stable hex
// `note_id` (`id`).
export interface Note {
  id: string;
  title: string;
  body: string;
  tags?: string[];
  updatedAt: number;
}

// Activity reminder persisted on web — mirrors `ReminderRecord` in @ping/ui.
// DeviceGroup-only personal state (§2.10). Keyed on the stable hex
// `reminder_id` (`id`). Indexed on `fireAt` so the local scheduler can
// cheaply find what's due next.
export interface Reminder {
  id: string;
  conversationId: string;
  messageId?: string;
  fireAt: number;
  note?: string;
  createdAt: number;
  firedAt?: number;
}

// Per-conversation read pointer persisted on web — mirrors
// `ReadPointerRecord` in @ping/ui. DeviceGroup-only personal state
// (§2.10). Keyed on `conversationId`; last-writer-wins on `lastReadAt`.
export interface ReadPointer {
  conversationId: string;
  lastReadMessageId?: string;
  lastReadAt: number;
  updatedAt: number;
}

/** Persisted "Call Notes" doc for one call — saved on call teardown so the
 *  in-chat call tile can preview + download it later. Keyed by call id. */
export interface CallNote {
  /** = call_id (lowercase hex). */
  id: string;
  conversationId: string;
  title: string;
  text: string;
  items: { id: string; text: string; done: boolean }[];
  updatedAt: number;
}

export interface Draft {
  conversationId: string;
  body: string;
  updatedAt: number;
}

export interface AttachmentMeta {
  id: string;
  messageId: string;
  state: 'pending' | 'uploading' | 'uploaded' | 'failed';
  filename?: string;
  mimeType?: string;
  sizeBytes?: number;
  createdAt: number;
}

export interface SettingsKV {
  key: string;
  value: unknown;
  updatedAt: number;
}

export interface CallHistoryEntry {
  id: string;
  callSessionId: string;
  conversationId: string;
  participantAccountIds: string[];
  type: 'incoming' | 'outgoing' | 'missed';
  isVideo: boolean;
  startedAt: number;
  endedAt?: number;
  durationSeconds?: number;
}

/** Network-fetched call log row, cached in Dexie for offline-first display. */
export interface RemoteCallHistoryRow {
  id: string;
  peerAccountId: string;
  peerDisplayName: string;
  peerAvatarUri?: string;
  direction: 'incoming' | 'outgoing' | 'missed';
  kind: 'audio' | 'video';
  timestamp: number;
  durationSec?: number;
  // Full group-call roster (display name + avatar per member) so the avatar
  // stack survives the offline-cache round-trip. Non-indexed; absent for 1:1s.
  participants?: Array<{ displayName: string; avatarUri?: string }>;
  syncedAt: number;
}

export interface Space {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  order: number;
  createdAt: number;
  updatedAt: number;
}

export interface Folder {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  order: number;
  createdAt: number;
  updatedAt: number;
}

export interface ConversationSpaceMapping {
  conversationId: string;
  spaceId: string;
  addedAt: number;
}

export interface ConversationFolderMapping {
  conversationId: string;
  folderId: string;
  addedAt: number;
}

// Durable cache of decrypted media THUMBNAILS (video posters + doc
// first-page renders) so a preview survives a reload without re-fetching
// + re-decrypting (or re-rasterizing) from S3 — the user should never see
// a video/doc tile without its preview once it's been resolved once.
// Keyed by a stable per-message key; stores the small image blob.
export interface MediaThumb {
  key: string;
  blob: Blob;
  mime: string;
  updatedAt: number;
}

/**
 * Persistent cache of DECRYPTED message media (images/video/docs), keyed by the
 * stable `mediaId`. Lets a previously-viewed attachment render INSTANTLY and
 * OFFLINE instead of re-downloading + re-decrypting on every open/reload. Same
 * local-plaintext-at-rest posture as the `messages` (plaintext body) and
 * `media_thumbnails` (decrypted poster) tables; wiped with the whole DB on sign
 * out. LRU-evicted (oldest `updatedAt` first) to bound disk use.
 */
export interface MediaBlob {
  mediaId: string;
  blob: Blob;
  mime: string;
  sizeBytes: number;
  updatedAt: number;
}

// Offline-first business directory records.
// `DirectoryCompanyRecord` — public listing cache from
// GET /v1/directory/companies; background-synced with a 5-minute TTL.
// `MyCompanyRecord` — the user's own companies; written optimistically on
// create and flipped to 'synced' once the server confirms.
export interface DirectoryCompanyRecord {
  id: string;
  ownerAccountId: string;
  name: string;
  description: string;
  website: string | null;
  logoUrl: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  employeeRange: string | null;
  sector: string | null;
  foundedYear: number | null;
  categorySlugs: string[];
  categoryNames: string[];
  verified: boolean;
  createdAt: string;
  updatedAt: string;
  syncedAt: number;
}

export interface MyCompanyRecord extends DirectoryCompanyRecord {
  syncState: 'synced' | 'pending' | 'failed';
}

/**
 * Outbox row — a message that has been written to the local thread (its bubble
 * shows `pending`) but not yet confirmed sent over the wire. Persisted so a
 * send made while offline survives a reload and is replayed when connectivity
 * returns. Keyed by the local-echo message id so the drainer can patch the same
 * `messages` row via `commitSentMessage` and avoid duplicate bubbles.
 */
export interface OutboxItem {
  /** Same id as the optimistic `messages` row (local-echo id). */
  id: string;
  conversationId: string;
  conversationName?: string;
  /** Exactly the bytes `conv.send()` was given — replayed verbatim. */
  envelopeBytes: Uint8Array;
  /** Human-readable body for the conversation-row preview after commit. */
  body: string;
  /** First-enqueued time — the drain order (FIFO per conversation). */
  createdAt: number;
  /** How many send attempts have failed (drives exponential backoff). */
  attempts: number;
  /** Epoch ms before which the drainer should not retry this row. */
  nextAttemptAt: number;
}

const db = new Dexie('ping-persistence') as Dexie & {
  outbox: EntityTable<OutboxItem, 'id'>;
  media_blobs: EntityTable<MediaBlob, 'mediaId'>;
  media_thumbnails: EntityTable<MediaThumb, 'key'>;
  accounts: EntityTable<Account, 'id'>;
  directory_companies: EntityTable<DirectoryCompanyRecord, 'id'>;
  my_companies: EntityTable<MyCompanyRecord, 'id'>;
  devices: EntityTable<Device, 'id'>;
  conversations: EntityTable<Conversation, 'id'>;
  messages: EntityTable<Message, 'id'>;
  // Compound primary key `[conversationId+targetContentHash+emoji+actorAccountId]`
  // — Dexie's TS helper only models a single-field PK, so we point the
  // generic at `id` (a real string field on the row) and declare the
  // true compound key in the `.stores(...)` schema string below.
  reactions: EntityTable<Reaction, 'id'>;
  // Primary key `&id` (`${pollTarget}:${actorAccountId}:${optionIndex}`) so
  // multi-select polls can hold one row per (actor, option); the
  // `[pollTarget+actorAccountId]` compound index backs the by-actor
  // delete/replace. See the `.stores(...)` schema string below.
  poll_votes: EntityTable<PollVote, 'id'>;
  // Primary key `&id` (the create/sidecar message content hash) so a true
  // re-delivery dedupes; `conversationId` indexes the by-conversation fold and
  // `meetingId` the per-meeting lookup. See the `.stores(...)` string below.
  meeting_events: EntityTable<MeetingEvent, 'id'>;
  // Device-local meeting reminders (§2.13), keyed on `meetingId`; `startsAt`
  // indexes the scheduler's due-soonest query.
  meeting_reminders: EntityTable<MeetingReminder, 'meetingId'>;
  tasks: EntityTable<Task, 'id'>;
  notes: EntityTable<Note, 'id'>;
  reminders: EntityTable<Reminder, 'id'>;
  read_pointers: EntityTable<ReadPointer, 'conversationId'>;
  drafts: EntityTable<Draft, 'conversationId'>;
  attachments_meta: EntityTable<AttachmentMeta, 'id'>;
  settings_kv: EntityTable<SettingsKV, 'key'>;
  call_history: EntityTable<CallHistoryEntry, 'id'>;
  call_notes: EntityTable<CallNote, 'id'>;
  remote_call_history: EntityTable<RemoteCallHistoryRow, 'id'>;
  spaces: EntityTable<Space, 'id'>;
  folders: EntityTable<Folder, 'id'>;
  // Compound primary key — Dexie's TypeScript helpers only model single-field
  // primary keys, so we point the generic at `conversationId` and rely on the
  // string in `db.version(N).stores(...)` to declare the real `[a+b]` key.
  conversation_space_mappings: EntityTable<ConversationSpaceMapping, 'conversationId'>;
  conversation_folder_mappings: EntityTable<ConversationFolderMapping, 'conversationId'>;
};

db.version(1).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
});

db.version(2).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
});

db.version(3).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
});

// v4: add `lastMessagePreview` + `lastMessageSenderAccountId` to
// `conversations`. Dexie doesn't require listing every column in
// `.stores()` — it only needs indexed fields. Both new fields are
// READ on every chat-list row but never QUERIED on, so they live
// outside the schema string and are simply patched into the row at
// write time. The version bump is still needed to let Dexie run a
// no-op migration that opens the existing DB at the new version.
db.version(4).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
});

// v5: optional `memberAccountIds` on group conversations.
// No new indexes — dedup scans `kind === 'group'` rows (≤ few dozen).
db.version(5).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
});

// v6: `memberDisplayNames` + `memberDeviceNames` for group list / bubble labels.
db.version(6).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
});

// v7: persistent CRDT reactions (OR-set). New `reactions` table keyed on
// the compound `[conversationId+targetContentHash+emoji+actorAccountId]`
// so adds are idempotent and removes are exact-row deletes; the
// `[conversationId+targetContentHash]` index serves the per-message
// projection and `conversationId` backs `listByConversation` + the
// liveQuery subscription. The new optional Message fields
// (`inReplyTo` / `editedAt` / `redactedAt`) are non-indexed passthroughs
// and need no schema-string change — listing the prior tables unchanged
// lets Dexie run a no-op migration that just creates the new table.
db.version(7).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  reactions:
    '&[conversationId+targetContentHash+emoji+actorAccountId], conversationId, [conversationId+targetContentHash]',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
});

// v8: poll votes (OR-set, single-choice) + personal tasks/notes
// (DeviceGroup-only, §2.10). `poll_votes` is keyed on the compound
// `[pollTarget+actorAccountId]` so a re-vote replaces the actor's prior
// row and `deleteByActor` is an exact-row delete; the `pollTarget` index
// serves `listByPoll` + the liveQuery subscription. `tasks` / `notes`
// are keyed on the stable hex id (`task_id` / `note_id`) — the whole
// per-user set is read at once, so no secondary indexes are declared.
// Prior tables are listed unchanged so Dexie runs a no-op migration that
// just creates the three new tables.
db.version(8).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  reactions:
    '&[conversationId+targetContentHash+emoji+actorAccountId], conversationId, [conversationId+targetContentHash]',
  poll_votes: '&[pollTarget+actorAccountId], pollTarget',
  tasks: '&id',
  notes: '&id',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
});

// v9: durable media-thumbnail cache (video posters + doc first-page
// renders). Keyed by a stable per-message key; stores the small image
// blob so the preview survives reloads without re-fetching from S3. Prior
// tables listed unchanged so Dexie runs a no-op migration that just adds
// the new table.
db.version(9).stores({
  accounts: '&id, phoneE164, email, deletedAt',
  devices: '&id, accountId, lastSeenAt',
  conversations: '&id, kind, archivedAt, lastMessageAt, [archivedAt+lastMessageAt]',
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], &contentHash, deletedAt',
  reactions:
    '&[conversationId+targetContentHash+emoji+actorAccountId], conversationId, [conversationId+targetContentHash]',
  poll_votes: '&[pollTarget+actorAccountId], pollTarget',
  tasks: '&id',
  notes: '&id',
  drafts: '&conversationId, updatedAt',
  attachments_meta: '&id, messageId, state',
  settings_kv: '&key, updatedAt',
  call_history: '&id, &callSessionId, conversationId, type, startedAt',
  spaces: '&id, order',
  folders: '&id, order',
  conversation_space_mappings: '[conversationId+spaceId], conversationId, spaceId',
  conversation_folder_mappings: '[conversationId+folderId], conversationId, folderId',
  media_thumbnails: '&key, updatedAt',
});

// v10: drop the UNIQUE constraint on `contentHash` (now a plain index).
// `content_hash` is computed by the SDK over the plaintext ALONE
// (`SHA-256(kind || payload)`), so two distinct messages with identical text
// ("hi", "ok") share a content_hash. The old `&contentHash` unique index
// rejected the repeat outright, so repeated messages silently failed to
// persist (and to send — the sender's own echo errored). Dedupe of a TRUE
// re-delivery is now handled by `persistIncoming` (match on contentHash AND
// `sentAt`, the HLC wall-clock — a repeat has a different sentAt), and the
// transport dedupes on `content_hash+hlc` so each envelope reaches the SDK
// only once (no concurrent re-insert race). The plain `contentHash` index is
// kept for receipt lookups (`getByContentHash`).
db.version(10).stores({
  messages: '&id, conversationId, [conversationId+epoch+serverSeq], contentHash, deletedAt',
});

// v11: per-call "Call Notes" doc, saved on call teardown for later
// preview/download from the in-chat call tile.
db.version(11).stores({
  call_notes: '&id, conversationId, updatedAt',
});

// v12: network-fetched call history cache. Stores the server-side call
// log (peerAccountId, direction, kind, timestamp) denormalized with peer
// display info so the /calls route can render immediately on next load
// without waiting for a network round-trip. Background sync writes here;
// useLiveQuery in useCallHistory re-renders the list reactively.
db.version(12).stores({
  remote_call_history: '&id, timestamp, direction',
});

// v13: offline-first business directory.
// `my_companies` — the user's own companies; optimistic writes on create,
// flipped to 'synced' on server confirmation. Indexed on syncState so the
// background sync loop can cheaply query pending/failed rows.
// `directory_companies` — public listing cache from
// GET /v1/directory/companies; TTL-based refresh, keyed by id.
db.version(13).stores({
  my_companies: '&id, syncState, syncedAt',
  directory_companies: '&id, verified, syncedAt',
});

// v14: offline send OUTBOX. A send made while offline writes its optimistic
// bubble to `messages` (deliveryState: 'pending') AND an outbox row carrying
// the exact `conv.send()` bytes. The drainer (sendOutbox.ts) replays these on
// reconnect / `online` / app boot, FIFO per conversation, then deletes the row.
// Indexed on conversationId (per-conversation ordering) + nextAttemptAt
// (backoff scheduling).
db.version(14).stores({
  outbox: '&id, conversationId, createdAt, nextAttemptAt',
});

// v15: persistent decrypted-media cache. A viewed attachment's decrypted blob
// is stored keyed by mediaId so it renders instantly + offline on the next
// open/reload. Indexed on updatedAt for LRU eviction.
db.version(15).stores({
  media_blobs: '&mediaId, updatedAt',
});

// v16: synced personal state for the Activity feature (DeviceGroup-only,
// §2.10). `reminders` — "remind me about this" rows keyed on the stable hex
// `reminder_id`, indexed on `fireAt` so the local scheduler finds what's due.
// `read_pointers` — per-conversation read position keyed on `conversationId`,
// indexed on `updatedAt`. Both arrive over the DeviceGroup so a reminder set /
// item read on one device mirrors to the user's other devices.
db.version(16).stores({
  reminders: '&id, conversationId, fireAt',
  read_pointers: '&conversationId, updatedAt',
});

// v17 + v18: multi-select polls. A multi-select vote is an OR-set of one
// row PER (actor, option), so `poll_votes` must allow several rows per
// actor — the old compound primary key `[pollTarget+actorAccountId]`
// caps it at one. Dexie can't change a store's primary key in place, so
// v17 drops the (locally-cached, re-established-on-next-vote) table and
// v18 recreates it keyed on the stable `&id`
// (`${pollTarget}:${actorAccountId}:${optionIndex}`), with a
// `[pollTarget+actorAccountId]` compound index for the by-actor
// delete/replace and the `pollTarget` index for `listByPoll` + the
// liveQuery. Single-choice stays the one-row-per-actor special case, so
// no data transform is needed — delete-then-insert re-establishes rows.
db.version(17).stores({
  poll_votes: null,
});

db.version(18).stores({
  poll_votes: '&id, pollTarget, [pollTarget+actorAccountId]',
});

// Meeting events (§2.13, 0xC2–0xC5) — the host folds these per conversation
// through `reduceMeeting` to derive live meeting + RSVP card state.
db.version(19).stores({
  meeting_events: '&id, conversationId, meetingId',
});

// Device-local meeting reminders (§2.13) — creator's per-device "remind me"
// choice; `startsAt` indexes the scheduler's arm query.
db.version(20).stores({
  meeting_reminders: '&meetingId, conversationId, startsAt',
});

/**
 * Durable thumbnail cache. `get` returns the cached image Blob (or
 * undefined); `put` stores/overwrites it. Both are best-effort — a cache
 * miss/error just means the caller re-derives the thumbnail. Used by the
 * chat-room video-poster + doc-preview resolvers so a tile never loses its
 * preview across reloads.
 */
/**
 * Per-call "Call Notes" persistence. Saved on call teardown (best-effort) and
 * read by the in-chat call tile's footer to preview + download. Keyed by call id.
 */
export const webCallNotesPersister = {
  async put(note: CallNote): Promise<void> {
    try {
      await db.call_notes.put(note);
    } catch {
      // best-effort — a quota/serialization failure just skips saving.
    }
  },
  async get(callId: string): Promise<CallNote | undefined> {
    try {
      return await db.call_notes.get(callId);
    } catch {
      return undefined;
    }
  },
};

export const webMediaThumbCache = {
  async get(key: string): Promise<Blob | undefined> {
    try {
      const row = await db.media_thumbnails.get(key);
      return row?.blob;
    } catch {
      return undefined;
    }
  },
  async put(key: string, blob: Blob, mime: string): Promise<void> {
    try {
      await db.media_thumbnails.put({ key, blob, mime, updatedAt: Date.now() });
    } catch {
      // best-effort — quota/serialization failure just skips the cache.
    }
  },
};

// Bound the decrypted-media cache so it can't grow without limit. Full-res
// media is far larger than thumbnails, so cap both count and total bytes;
// evict the least-recently-updated rows past the cap.
const MEDIA_BLOB_CACHE_MAX_ENTRIES = 300;
const MEDIA_BLOB_CACHE_MAX_BYTES = 200 * 1024 * 1024; // 200 MB

async function evictMediaBlobsIfNeeded(): Promise<void> {
  try {
    const rows = await db.media_blobs.orderBy('updatedAt').toArray(); // oldest first
    let totalBytes = rows.reduce((n, r) => n + (r.sizeBytes ?? 0), 0);
    let count = rows.length;
    const doomed: string[] = [];
    for (const r of rows) {
      if (count <= MEDIA_BLOB_CACHE_MAX_ENTRIES && totalBytes <= MEDIA_BLOB_CACHE_MAX_BYTES) break;
      doomed.push(r.mediaId);
      count -= 1;
      totalBytes -= r.sizeBytes ?? 0;
    }
    if (doomed.length > 0) await db.media_blobs.bulkDelete(doomed);
  } catch {
    // best-effort — eviction failure just means the cache is briefly larger.
  }
}

/** Persistent decrypted-media cache keyed by `mediaId`. `get` returns the cached
 *  Blob (or undefined); `put` stores it and LRU-evicts past the size caps. Both
 *  are best-effort — a miss/error just re-derives the media from the network. */
export const webMediaBlobCache = {
  async get(mediaId: string): Promise<Blob | undefined> {
    try {
      const row = await db.media_blobs.get(mediaId);
      if (row === undefined) return undefined;
      // Touch updatedAt so frequently-viewed media survives LRU eviction.
      void db.media_blobs.update(mediaId, { updatedAt: Date.now() }).catch(() => undefined);
      return row.blob;
    } catch {
      return undefined;
    }
  },
  async put(mediaId: string, blob: Blob, mime: string): Promise<void> {
    try {
      await db.media_blobs.put({
        mediaId,
        blob,
        mime,
        sizeBytes: blob.size,
        updatedAt: Date.now(),
      });
      await evictMediaBlobsIfNeeded();
    } catch {
      // best-effort — quota/serialization failure just skips the cache.
    }
  },
};

// Dexie-backed implementation of the shared `ReactionsPersister`. `put`
// relies on the compound PK for idempotency; `deleteByKey` deletes the
// exact OR-set element; `listByConversation` reads every reaction row for
// the conversation; `subscribe` wraps the same read in a Dexie liveQuery
// (same pattern as the messages adapter) so `useReactions` re-projects
// live. The PK lives on the row as the natural-key tuple, but `put`
// callers also pass a stable `id` string — Dexie ignores `id` for keying
// here (the schema declares the compound PK) and just stores it as a
// field, which keeps the row shape identical to `ReactionRecord`.
export const webReactionsPersister: ReactionsPersister = {
  async put(r: ReactionRecord) {
    await db.reactions.put({
      id: r.id,
      conversationId: r.conversationId,
      targetContentHash: r.targetContentHash,
      ...(r.targetMessageId !== undefined ? { targetMessageId: r.targetMessageId } : {}),
      emoji: r.emoji,
      actorAccountId: r.actorAccountId,
      addedAt: r.addedAt,
    });
  },
  async deleteByKey(conversationId, targetHash, emoji, actorAccountId) {
    // The compound primary key is an array at runtime, but Dexie's
    // `EntityTable` TS helper only models the single-field PK we pointed
    // its generic at (`id`) — see the table-typing note above. Cast the
    // real compound key through `unknown` to satisfy the narrower type.
    await db.reactions.delete(
      [conversationId, targetHash, emoji, actorAccountId] as unknown as string,
    );
  },
  async listByConversation(conversationId) {
    return db.reactions.where('conversationId').equals(conversationId).toArray();
  },
  subscribe(conversationId, cb) {
    const subscription = liveQuery(() =>
      db.reactions.where('conversationId').equals(conversationId).toArray(),
    ).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `PollVotesPersister`. `put` upserts one vote row on its
// `&id` key; `deleteByActor` clears ALL of an actor's rows via the
// `[pollTarget+actorAccountId]` index (so it also sweeps legacy
// single-choice `${pollTarget}:${actorAccountId}` rows); `replaceActorVotes`
// does an atomic OR-set replace (delete-then-insert) for multi-select;
// `listByPoll` reads every vote for the poll; `subscribe` wraps the same
// read in a liveQuery so `usePollVotes` re-projects live.
export const webPollVotesPersister: PollVotesPersister = {
  async put(r: PollVoteRecord) {
    await db.poll_votes.put({
      id: r.id,
      pollTarget: r.pollTarget,
      actorAccountId: r.actorAccountId,
      optionIndex: r.optionIndex,
      votedAt: r.votedAt,
    });
  },
  async deleteByActor(pollTarget, actorAccountId) {
    // Delete every row this actor holds on the poll (multi-select) — and
    // any legacy single-choice row keyed `${pollTarget}:${actorAccountId}`
    // — by the field-pair compound index, not the `&id` key.
    await db.poll_votes
      .where('[pollTarget+actorAccountId]')
      .equals([pollTarget, actorAccountId])
      .delete();
  },
  async replaceActorVotes(pollTarget, actorAccountId, optionIndices, votedAt) {
    // Atomic OR-set replace: clear the actor's existing rows then insert
    // one row per chosen option, all inside a single readwrite tx so the
    // liveQuery projection never observes a half-applied selection.
    await db.transaction('rw', db.poll_votes, async () => {
      await db.poll_votes
        .where('[pollTarget+actorAccountId]')
        .equals([pollTarget, actorAccountId])
        .delete();
      if (optionIndices.length > 0) {
        await db.poll_votes.bulkPut(
          optionIndices.map((optionIndex) => ({
            id: `${pollTarget}:${actorAccountId}:${optionIndex}`,
            pollTarget,
            actorAccountId,
            optionIndex,
            votedAt,
          })),
        );
      }
    });
  },
  async listByPoll(pollTarget) {
    return db.poll_votes.where('pollTarget').equals(pollTarget).toArray();
  },
  subscribe(pollTarget, cb) {
    const subscription = liveQuery(() =>
      db.poll_votes.where('pollTarget').equals(pollTarget).toArray(),
    ).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `MeetingEventsPersister` (§2.13). `put` upserts one event on
// its `&id` key (the message content hash — a true re-delivery dedupes);
// `listByConversation` reads every event for the conversation; `subscribe`
// wraps the same read in a liveQuery so the host re-folds live meeting state.
export const webMeetingEventsPersister: MeetingEventsPersister = {
  async put(r: MeetingEventRecord) {
    await db.meeting_events.put({
      id: r.id,
      conversationId: r.conversationId,
      meetingId: r.meetingId,
      kind: r.kind,
      senderAccountId: r.senderAccountId,
      ...(r.senderDeviceId !== undefined ? { senderDeviceId: r.senderDeviceId } : {}),
      ...(r.senderUserIdHex !== undefined ? { senderUserIdHex: r.senderUserIdHex } : {}),
      at: r.at,
      payloadJson: r.payloadJson,
    });
  },
  async listByConversation(conversationId) {
    return db.meeting_events.where('conversationId').equals(conversationId).toArray();
  },
  async deleteByMeeting(meetingId) {
    await db.meeting_events.where('meetingId').equals(meetingId).delete();
  },
  subscribe(conversationId, cb) {
    const subscription = liveQuery(() =>
      db.meeting_events.where('conversationId').equals(conversationId).toArray(),
    ).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Device-local meeting-reminder store (§2.13). `put` upserts on `&meetingId`;
// `list` reads every reminder (the global scheduler arms them all); `subscribe`
// wraps a liveQuery so the scheduler re-arms and the card re-renders the
// reminder row live; `remove` drops it (meeting canceled).
export const webMeetingRemindersPersister = {
  async put(r: MeetingReminder): Promise<void> {
    await db.meeting_reminders.put({
      meetingId: r.meetingId,
      conversationId: r.conversationId,
      title: r.title,
      startsAt: r.startsAt,
      reminderMinutes: r.reminderMinutes,
      ...(r.firedAt !== undefined ? { firedAt: r.firedAt } : {}),
    });
  },
  async remove(meetingId: string): Promise<void> {
    await db.meeting_reminders.delete(meetingId);
  },
  async list(): Promise<readonly MeetingReminder[]> {
    return db.meeting_reminders.toArray();
  },
  subscribe(cb: (rows: readonly MeetingReminder[]) => void): () => void {
    const subscription = liveQuery(() => db.meeting_reminders.toArray()).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `TasksPersister` (DeviceGroup-only personal state).
// Keyed on `id` (hex `task_id`); `put` is last-writer-wins, `delete`
// removes the row, `list` reads the whole set, `subscribe` re-renders
// `useTasks` on every change.
export const webTasksPersister: TasksPersister = {
  async put(r: TaskRecord) {
    await db.tasks.put({
      id: r.id,
      title: r.title,
      position: r.position,
      updatedAt: r.updatedAt,
      ...(r.due !== undefined ? { due: r.due } : {}),
      ...(r.done !== undefined ? { done: r.done } : {}),
      ...(r.list !== undefined ? { list: r.list } : {}),
    });
  },
  async delete(id) {
    await db.tasks.delete(id);
  },
  async list() {
    return db.tasks.toArray();
  },
  subscribe(cb) {
    const subscription = liveQuery(() => db.tasks.toArray()).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `NotesPersister` (DeviceGroup-only personal state).
// Keyed on `id` (hex `note_id`); same contract as the tasks persister.
export const webNotesPersister: NotesPersister = {
  async put(r: NoteRecord) {
    await db.notes.put({
      id: r.id,
      title: r.title,
      body: r.body,
      updatedAt: r.updatedAt,
      ...(r.tags !== undefined ? { tags: r.tags } : {}),
    });
  },
  async delete(id) {
    await db.notes.delete(id);
  },
  async list() {
    return db.notes.toArray();
  },
  subscribe(cb) {
    const subscription = liveQuery(() => db.notes.toArray()).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `RemindersPersister` (DeviceGroup-only personal state).
// Keyed on `id` (hex `reminder_id`); `put` is last-writer-wins, `delete`
// removes the row (REMINDER_CLEAR), `list` reads the whole set, `subscribe`
// re-renders the Activity feed / scheduler on every change.
export const webRemindersPersister: RemindersPersister = {
  async put(r: ReminderRecord) {
    await db.reminders.put({
      id: r.id,
      conversationId: r.conversationId,
      fireAt: r.fireAt,
      createdAt: r.createdAt,
      ...(r.messageId !== undefined ? { messageId: r.messageId } : {}),
      ...(r.note !== undefined ? { note: r.note } : {}),
      ...(r.firedAt !== undefined ? { firedAt: r.firedAt } : {}),
    });
  },
  async delete(id) {
    await db.reminders.delete(id);
  },
  async list() {
    return db.reminders.toArray();
  },
  subscribe(cb) {
    const subscription = liveQuery(() => db.reminders.toArray()).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `ReadPointersPersister` (DeviceGroup-only personal state).
// Keyed on `conversationId`. `put` advances the pointer monotonically —
// an incoming pointer with an older `lastReadAt` than the stored one is a
// no-op so a slow sibling device can never regress read state.
export const webReadPointersPersister: ReadPointersPersister = {
  async put(r: ReadPointerRecord) {
    await db.transaction('rw', db.read_pointers, async () => {
      const existing = await db.read_pointers.get(r.conversationId);
      if (existing !== undefined && existing.lastReadAt >= r.lastReadAt) return;
      await db.read_pointers.put({
        conversationId: r.conversationId,
        lastReadAt: r.lastReadAt,
        updatedAt: r.updatedAt,
        ...(r.lastReadMessageId !== undefined
          ? { lastReadMessageId: r.lastReadMessageId }
          : {}),
      });
    });
  },
  async get(conversationId) {
    return db.read_pointers.get(conversationId);
  },
  async list() {
    return db.read_pointers.toArray();
  },
  subscribe(cb) {
    const subscription = liveQuery(() => db.read_pointers.toArray()).subscribe({
      next: (rows) => {
        if (rows) cb(rows);
      },
      error: (error) => {
        throw error;
      },
    });
    return () => {
      subscription.unsubscribe();
    };
  },
};

// Dexie-backed `TourSeenPersister` (DeviceGroup-only personal state). Instead of
// its own table, an incoming `com.ping.tour_seen` writes the SAME `settings_kv`
// flag the tour's local gate already reads (`tour.${tourId}.seen`), so a tour
// finished on a sibling device is picked up automatically here. Best-effort.
export const webTourSeenPersister: TourSeenPersister = {
  async put({ tourId, seenAt }) {
    try {
      await db.settings_kv.put({
        key: `tour.${tourId}.seen`,
        value: { seenAt },
        updatedAt: Date.now(),
      });
    } catch {
      /* best-effort — a failed flag write just leaves the tour ungated here */
    }
  },
};

export { db };
